import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  BackgroundVariant,
  useReactFlow,
  useNodesState,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import IdeaNode from './IdeaNode';
import { getNodeConfig } from './nodeConfig';
import { computeLayout, buildEdges } from './layoutUtils';
import NoveltyFilterBar from './NoveltyFilterBar';
import './FlowchartView.css';

const nodeTypes = { ideaNode: IdeaNode };
const proOptions = { hideAttribution: true };
const fitViewOpts = { padding: 0.2, duration: 400 };

/* ── AutoFitView — refits on node count change (only when NOT generating) */
function AutoFitView({ nodeCount, isGenerating }) {
  const { fitView } = useReactFlow();
  const prevCount = useRef(nodeCount);
  const mountedRef = useRef(false);
  const prevGenerating = useRef(isGenerating);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    // Fit once when generation ends
    if (prevGenerating.current && !isGenerating && nodeCount > 0) {
      const timer = setTimeout(() => {
        fitView({ padding: 0.15, duration: 500, maxZoom: 1.2 });
      }, 300);
      prevGenerating.current = isGenerating;
      prevCount.current = nodeCount;
      return () => clearTimeout(timer);
    }
    prevGenerating.current = isGenerating;
    // During generation, skip auto-fit (auto-follow handles it)
    if (isGenerating) {
      prevCount.current = nodeCount;
      return;
    }
    if (nodeCount !== prevCount.current) {
      fitView({ padding: 0.15, duration: 400, maxZoom: 1.2 });
      prevCount.current = nodeCount;
    }
  }, [nodeCount, isGenerating, fitView]);
  return null;
}

/* ── AutoFollowNode — pans to newest node during generation ── */
function AutoFollowNode({ nodes, isGenerating, autoFollow }) {
  const { setCenter, getZoom } = useReactFlow();
  const prevNodeCount = useRef(nodes.length);
  const animFrameRef = useRef(null);

  useEffect(() => {
    if (!isGenerating || !autoFollow || nodes.length === 0) {
      prevNodeCount.current = nodes.length;
      return;
    }
    if (nodes.length > prevNodeCount.current) {
      const newest = nodes[nodes.length - 1];
      if (newest?.position) {
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        animFrameRef.current = requestAnimationFrame(() => {
          const zoom = getZoom();
          setCenter(
            newest.position.x + 130,
            newest.position.y + 50,
            { zoom: Math.max(zoom, 0.7), duration: 300 }
          );
        });
      }
    }
    prevNodeCount.current = nodes.length;
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [nodes, isGenerating, autoFollow, setCenter, getZoom]);

  return null;
}

/* ── Breadcrumb rail: ancestor path of selected node ───── */
function BreadcrumbRail({ selectedNode, nodes, onNodeClick }) {
  const { setCenter, getZoom } = useReactFlow();

  if (!selectedNode) return null;

  // Build ancestor chain by walking up parentIds
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  const chain = [];
  const visited = new Set();
  let current = nodeMap[selectedNode.id];
  while (current) {
    if (visited.has(current.id)) break;
    visited.add(current.id);
    chain.unshift(current);
    const parentIds = current.data?.parentIds || (current.data?.parentId ? [current.data.parentId] : []);
    current = parentIds[0] ? nodeMap[parentIds[0]] : null;
  }

  if (chain.length <= 1) return null;

  const handleCrumbClick = (node) => {
    onNodeClick(null, node);
    if (node.position) {
      const zoom = getZoom();
      setCenter(
        node.position.x + 110,
        node.position.y + 34,
        { zoom: Math.max(zoom, 0.8), duration: 400 }
      );
    }
  };

  return (
    <Panel position="top-center" style={{ margin: '8px 0 0 0' }}>
      <div className="breadcrumb-rail">
        {chain.map((node, i) => {
          const config = getNodeConfig(node.data?.type);
          const isLast = i === chain.length - 1;
          return (
            <React.Fragment key={node.id}>
              {i > 0 && <span className="breadcrumb-rail-sep">→</span>}
              <button
                className={`breadcrumb-rail-crumb ${isLast ? 'breadcrumb-rail-active' : ''}`}
                onClick={() => handleCrumbClick(node)}
                title={node.data?.label}
              >
                <span style={{ color: config.color, fontSize: 10 }}>{config.icon}</span>
                <span className="breadcrumb-rail-label">
                  {(node.data?.label || '').slice(0, 25)}{(node.data?.label || '').length > 25 ? '…' : ''}
                </span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </Panel>
  );
}

/* ── Toolbar ────────────────────────────────────────────── */
function FlowchartToolbar({ searchQuery, onSearchChange, nodeCount, onCollapseAll, onExpandAll, hasCollapsed, drillStack, onExitDrill, onJumpToBreadcrumb, isGenerating, autoFollow, onToggleAutoFollow }) {
  const { fitView } = useReactFlow();
  const handleFit = useCallback(() => fitView({ padding: 0.2, duration: 400 }), [fitView]);

  return (
    <Panel position="top-left" style={{ margin: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Drill breadcrumb */}
      {drillStack && drillStack.length > 0 && (
        <div className="drill-breadcrumb-bar">
          <button className="drill-back-btn" onClick={onExitDrill} title="Back to full tree">
            ← ROOT
          </button>
          {drillStack.map((entry, i) => (
            <React.Fragment key={entry.nodeId}>
              <span className="drill-crumb-sep">›</span>
              <button
                className="drill-crumb-label"
                onClick={() => onJumpToBreadcrumb(i)}
                style={{ fontWeight: i === drillStack.length - 1 ? 700 : 400 }}
                title={entry.nodeLabel}
              >
                {entry.nodeLabel}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
      <div className="flowchart-toolbar">
        <button onClick={handleFit} title="Fit entire tree in view">⊡ Fit</button>
        {isGenerating && (
          <button
            className={autoFollow ? 'follow-active' : ''}
            onClick={onToggleAutoFollow}
            title={autoFollow ? 'Auto-follow ON — click to pause' : 'Resume auto-follow'}
          >
            {autoFollow ? '⤓ Following' : '⤓ Follow'}
          </button>
        )}
        {hasCollapsed ? (
          <button onClick={onExpandAll} title="Expand all collapsed branches">⊞ Expand</button>
        ) : (
          <button onClick={onCollapseAll} title="Collapse all branches">⊟ Collapse</button>
        )}
        <input
          type="text"
          placeholder="Search nodes..."
          value={searchQuery || ''}
          onChange={e => onSearchChange?.(e.target.value)}
          title="Filter by label or reasoning"
        />
        {nodeCount > 0 && <span className="node-count">{nodeCount} nodes</span>}
      </div>
    </Panel>
  );
}

/* ── FlowchartView ──────────────────────────────────────── */
export default function FlowchartView({
  displayNodes,
  onNodeClick,
  onNodeDoubleClick,
  onNodeContextMenu,
  onCloseContextMenu,
  drillStack,
  onExitDrill,
  onJumpToBreadcrumb,
  searchQuery,
  onSearchChange,
  onCollapseAll,
  onExpandAll,
  hasCollapsed,
  onReactFlowReady,
  isGenerating = false,
  selectedNode = null,
}) {
  // Auto-follow state
  const [autoFollow, setAutoFollow] = useState(true);
  const prevGeneratingRef = useRef(isGenerating);

  // Reset auto-follow when generation starts
  useEffect(() => {
    if (isGenerating && !prevGeneratingRef.current) {
      setAutoFollow(true);
    }
    prevGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  const handleToggleAutoFollow = useCallback(() => {
    setAutoFollow(f => !f);
  }, []);

  // Detect user-initiated pan to pause auto-follow
  const handleMoveStart = useCallback((event) => {
    if (isGenerating && event?.type === 'pointerdown') {
      setAutoFollow(false);
    }
  }, [isGenerating]);

  // Compute dagre top-down layout from displayNodes
  const layoutResult = useMemo(() => {
    if (!displayNodes || !displayNodes.length) return { nodes: [], edges: [] };
    const edgeList = buildEdges(displayNodes, { nodeConfigGetter: getNodeConfig });
    const laidOut = computeLayout(displayNodes, edgeList);
    return { nodes: laidOut, edges: edgeList };
  }, [displayNodes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutResult.nodes);

  // Sync nodes when displayNodes change
  useEffect(() => {
    setNodes(layoutResult.nodes);
  }, [layoutResult, setNodes]);

  const edges = layoutResult.edges;
  const onEdgesChange = useCallback(() => {}, []);

  const miniMapNodeColor = useCallback((node) => {
    return getNodeConfig(node.data?.type).color;
  }, []);

  const handleNodeClick = useCallback((_, node) => {
    onNodeClick(node);
  }, [onNodeClick]);

  const handleNodeDoubleClick = useCallback((_, node) => {
    if (onNodeDoubleClick) onNodeDoubleClick(node.id);
  }, [onNodeDoubleClick]);

  const handleNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    if (onNodeContextMenu) onNodeContextMenu(node.id, event.clientX, event.clientY);
  }, [onNodeContextMenu]);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <NoveltyFilterBar />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={fitViewOpts}
        minZoom={0.05}
        maxZoom={2}
        proOptions={proOptions}
        style={{ background: '#0a0a0f' }}
        nodesDraggable
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onNodeContextMenu={handleNodeContextMenu}
        onMoveStart={handleMoveStart}
        onPaneClick={onCloseContextMenu}
      >
        <Background variant={BackgroundVariant.Dots} gap={28} size={1} color="#1e1e2e" />
        <AutoFitView nodeCount={nodes.length} isGenerating={isGenerating} />
        <AutoFollowNode nodes={nodes} isGenerating={isGenerating} autoFollow={autoFollow} />
        <FlowchartToolbar
          searchQuery={searchQuery ?? ''}
          onSearchChange={onSearchChange}
          nodeCount={nodes.length}
          onCollapseAll={onCollapseAll}
          onExpandAll={onExpandAll}
          hasCollapsed={hasCollapsed}
          drillStack={drillStack}
          onExitDrill={onExitDrill}
          onJumpToBreadcrumb={onJumpToBreadcrumb}
          isGenerating={isGenerating}
          autoFollow={autoFollow}
          onToggleAutoFollow={handleToggleAutoFollow}
        />
        {selectedNode && (
          <BreadcrumbRail
            selectedNode={selectedNode}
            nodes={nodes}
            onNodeClick={handleNodeClick}
          />
        )}
        <Controls
          style={{
            background: '#16161f',
            border: '1px solid #2a2a3a',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        />
        <MiniMap
          nodeColor={miniMapNodeColor}
          maskColor="rgba(10,10,15,0.85)"
          style={{
            background: '#111118',
            border: '1px solid #2a2a3a',
            borderRadius: '8px',
          }}
        />
      </ReactFlow>
    </div>
  );
}
